import { useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useFieldArray, useFormContext, useWatch, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { z } from 'zod';

import { notifyApiError, useNotifier } from '@/shared/data';
import { CheckboxListFormField, DateFormField, EntityPickerField, EntitySheet, SelectFormField, TextFormField, type EntitySource } from '@/shared/form';
import { AssignFields as SharedAssignFields } from '@/shared/record';
import { tenantSource } from '@/shared/reference';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { toast } from '@/shared/ui/toaster';
import { userGroupSearchSource } from '@/modules/user-group/public';
import { userOperatorSource } from '@/modules/user/public';

import { todayCivil } from '../credit-note.disapproval';
import { registeredReserveTokensQuery, useCreditNoteMutation } from '../credit-note.queries';
import { reserveTokenBody, tokenOptionLabel, tokenRowsSchema, type AssignValues, type ShareValues, type TokenRow } from './bar-bodies';
import { PERMISSION_LEVELS, PRINCIPAL_TYPES, TOKEN_OFFICES, TOKEN_TYPES, type BarOption } from './bar-options';

// Campos compostos dos formulários da barra (dentro do `FormProvider` do diálogo). A lista de caixas
// (multisseleção) é a `CheckboxListFormField` de `shared/form`.

export function useBarOptions(options: readonly BarOption[]) {
  const { i18n } = useLingui();
  return options.map((option) => ({ value: option.value, label: i18n._(option.label) }));
}

/** A lista de caixas foi para `shared/form` (10F); o nome antigo fica para os consumidores (pessoa, barra, abas). */
export const CheckboxListField = CheckboxListFormField;
export type { CheckOption } from '@/shared/form';

/** As linhas de token com "Adicionar" e a lixeira (`ReserveTokenComponent`, `TokenForm` do DoneRevisionForm). */
export function TokenRowsField({ name = 'details', today }: { readonly name?: string; readonly today: string }) {
  const { t } = useLingui();
  const { control } = useFormContext<Record<string, TokenRow[]>>();
  const { fields, append, remove } = useFieldArray({ control, name });
  const offices = useBarOptions(TOKEN_OFFICES);
  const types = useBarOptions(TOKEN_TYPES);
  return (
    <div className="flex flex-col gap-3">
      {fields.map((item, index) => {
        const number = index + 1;
        return (
          <section key={item.id} aria-label={t`Token #${number}`} className="flex flex-col gap-3 rounded-card border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{t`Token #${number}`}</p>
              <Button type="button" variant="ghost" size="icon" aria-label={t`Remover o token #${number}`} onClick={() => remove(index)}>
                <Trash2 aria-hidden />
              </Button>
            </div>
            <SelectFormField name={`${name}.${String(index)}.warrantyRegistrationOffice`} label={t`Sistema de averbação`} required options={offices} placeholder={t`Selecione`} />
            <SelectFormField name={`${name}.${String(index)}.tokenType`} label={t`Tipo do token`} required options={types} placeholder={t`Selecione`} />
            <TextFormField name={`${name}.${String(index)}.tokenValue`} label={t`Token`} required />
            <DateFormField name={`${name}.${String(index)}.expirationDate`} label={t`Data de expiração`} required />
          </section>
        );
      })}
      <Button type="button" variant="link" className="w-fit" onClick={() => append({ warrantyRegistrationOffice: null, tokenType: null, tokenValue: null, expirationDate: today })}>
        <Plus aria-hidden />
        {t`Adicionar`}
      </Button>
    </div>
  );
}

/**
 * "Token" (múltiplo) + "Adicionar token" + um "Código ADE - Token X" por escolhido, como
 * `ApproveMarginReverseForm`/`CompleteManualWarranty`. O código fica pela posição da escolha (`tokens.{i}`
 * do legado); `resetCodes` zera os códigos quando a escolha muda (o `setValue('tokens', …)` da garantia).
 */
export function RegisteredTokensField({ personId, enabled, resetCodes }: { readonly personId: string; readonly enabled: boolean; readonly resetCodes: boolean }) {
  const { t } = useLingui();
  const [adding, setAdding] = useState(false);
  const tokens = useQuery(registeredReserveTokensQuery(personId, enabled));
  const { control, setValue } = useFormContext<{ selectedTokens: string[]; warrantyCodes: string[] }>();
  const selected = useWatch({ control, name: 'selectedTokens' });
  const count = selected.length;
  useEffect(() => {
    if (resetCodes) setValue('warrantyCodes', []);
  }, [count, resetCodes, setValue]);
  const options = (tokens.data ?? []).map((token) => ({ value: token.result.Token.TokenValue, label: tokenOptionLabel(token) }));
  return (
    <>
      <div className="flex flex-col gap-1">
        <CheckboxListField<{ selectedTokens: string[] }> name="selectedTokens" label={t`Token`} required options={options} empty={tokens.isPending && enabled ? t`Carregando…` : t`Nenhum token cadastrado disponível`} />
        <Button type="button" variant="link" className="w-fit self-end" onClick={() => setAdding(true)}>
          <Plus aria-hidden />
          {t`Adicionar token`}
        </Button>
      </div>
      {selected.map((token, index) => (
        <TextFormField<{ warrantyCodes: string[] }> key={`${token}-${String(index)}`} name={`warrantyCodes.${index}`} label={t`Código ADE - Token ${token}`} />
      ))}
      <AddTokenSheet personId={personId} open={adding} onOpenChange={setAdding} />
    </>
  );
}

/** "Tipo de permissão" → o registro (operador, grupo ou correspondente) → "Nível de permissão" (ShareRecord.tsx). */
export function ShareFields({ optionalType = false }: { readonly optionalType?: boolean }) {
  const { t } = useLingui();
  const types = useBarOptions(PRINCIPAL_TYPES);
  const levels = useBarOptions(PERMISSION_LEVELS);
  const { control, setValue } = useFormContext<ShareValues>();
  const type = useWatch({ control, name: 'principalType' });
  const typeLabel = types.find((option) => option.value === type)?.label ?? t`Registro`;
  const source: EntitySource | undefined = type === 'User' ? (userOperatorSource(undefined) as EntitySource) : type === 'Group' ? (userGroupSearchSource(undefined) as EntitySource) : type === 'Tenant' ? (tenantSource as EntitySource) : undefined;
  return (
    <>
      <SelectFormField<ShareValues>
        name="principalType"
        label={t`Tipo de permissão`}
        required={!optionalType}
        options={types}
        placeholder={t`Selecione`}
        onValueChange={() => setValue('principalId', null)}
        {...(optionalType ? { clearable: true, onClear: () => setValue('principalId', null) } : {})}
      />
      {source ? (
        <>
          <EntityPickerField<ShareValues, unknown> key={type ?? ''} name="principalId" label={typeLabel} required source={source} />
          <SelectFormField<ShareValues> name="permissionLevelType" label={t`Nível de permissão`} required options={levels} placeholder={t`Selecione`} />
        </>
      ) : null}
    </>
  );
}

/** "Correspondente", "Operadores" e "Grupos" (AssignForm.tsx): operadores e grupos do correspondente escolhido. */
export function AssignFields() {
  const { t } = useLingui();
  return (
    <SharedAssignFields
      users={userOperatorSource}
      groups={userGroupSearchSource}
      tenantField={() => <EntityPickerField<AssignValues, unknown> name="tenant" label={t`Correspondente`} source={tenantSource as EntitySource} />}
    />
  );
}

/** Grava um valor no formulário do diálogo a partir de um campo composto. */
export function useSetField<V extends FieldValues>() {
  const { setValue } = useFormContext<V>();
  return (name: Path<V>, value: unknown) => setValue(name, value as PathValue<V, Path<V>>, { shouldDirty: true });
}

/** Seção com título dentro de um diálogo ("Compartilhar registro", "Cancelar garantias"). */
export function DialogSection({ title, children, className }: { readonly title: ReactNode; readonly children: ReactNode; readonly className?: string }) {
  return (
    <section className={cn('flex flex-col gap-4 border-t border-border pt-4', className)}>
      <h3 className="text-base font-semibold">{title}</h3>
      {children}
    </section>
  );
}

/**
 * O drawer "Adicionar token" aberto de dentro do aprovar reserva de margem e do aprovar garantia
 * (`actionGeneric === 'add-tokens'`, creditNoteActions.tsx:984-997): grava e volta, e o seletor recarrega.
 */
export function AddTokenSheet({ personId, open, onOpenChange }: { readonly personId: string; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }) {
  const { i18n, t } = useLingui();
  const notifier = useNotifier();
  const save = useCreditNoteMutation('requestReserveTokens', { success: false, error: false });
  const today = todayCivil();
  return (
    <EntitySheet<{ details: TokenRow[] }>
      open={open}
      onOpenChange={onOpenChange}
      title={t`Adicionar token`}
      schema={z.object({ details: tokenRowsSchema(i18n, today) })}
      defaultValues={{ details: [{ warrantyRegistrationOffice: null, tokenType: null, tokenValue: null, expirationDate: today }] }}
      submitLabel={t`Salvar`}
      cancelLabel={t`Fechar`}
      onSubmit={async (values) => {
        try {
          await save.mutateAsync({ personId, tokens: reserveTokenBody(values.details) });
          toast.success(t`Token consignado adicionado com sucesso`);
          return true;
        } catch (error) {
          notifyApiError(notifier, error);
          return false;
        }
      }}
    >
      <TokenRowsField today={today} />
    </EntitySheet>
  );
}
